import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  Button,
  ButtonGroup,
  Modal,
} from "@datapay-by-datacom/design-system";
import { Heading } from "react-aria-components";
import { inlineCompanyFieldIssues } from "../components/inlineCompanyFieldIssues";
import {
  cellErrorKey,
  CSV_HEADER_TO_FIELD_PATH,
  unknownShellCsvColumnCellKey,
} from "../hooks";
import type {
  CompanyShellCsvColumn,
  CompanyShellNormalisation,
} from "../types";
import {
  COMPANY_SHELL_IDENTITY_HEADERS,
  type CompanyShellDraft,
} from "./companyShellEditorDraft";
import s from "./CompanyShellEditModal.module.css";

interface CompanyShellEditGroup {
  id: string;
  title: string;
  headers: readonly string[];
}

interface CompanyShellEditModalProps {
  isOpen: boolean;
  rowIndex: number;
  sourceRow: number;
  columns: readonly CompanyShellCsvColumn[];
  groups: readonly CompanyShellEditGroup[];
  draft: CompanyShellDraft;
  rowIssues: readonly string[];
  cellErrors?: ReadonlyMap<string, string[]>;
  normalisations?: readonly CompanyShellNormalisation[];
  isDirty: boolean;
  isProcessing?: boolean;
  onDraftChange: (draft: CompanyShellDraft) => void;
  onCancel: () => void;
  onSave: () => void;
}

function headerToIdFragment(header: string): string {
  return header
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

function isBlank(value: string): boolean {
  return value.trim() === "";
}

/**
 * Focused whole-company editor that follows the Work Pattern Edit in UI modal
 * structure while retaining company-specific grouping and inline issue detail.
 */
export function CompanyShellEditModal({
  isOpen,
  rowIndex,
  sourceRow,
  columns,
  groups,
  draft,
  rowIssues,
  cellErrors,
  normalisations,
  isDirty,
  isProcessing = false,
  onDraftChange,
  onCancel,
  onSave,
}: CompanyShellEditModalProps) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const inputRefs = useRef(new Map<string, HTMLInputElement>());

  const columnsByHeader = useMemo(() => {
    const map = new Map<string, CompanyShellCsvColumn>();
    for (const column of columns) map.set(column.header, column);
    return map;
  }, [columns]);

  const normalisationsByCell = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of normalisations ?? []) {
      map.set(cellErrorKey(item.row_index, item.column), item.normalised_value);
    }
    return map;
  }, [normalisations]);

  const fieldReasons = useCallback(
    (header: string): string[] => {
      const fieldPath = Object.prototype.hasOwnProperty.call(
        CSV_HEADER_TO_FIELD_PATH,
        header
      )
        ? CSV_HEADER_TO_FIELD_PATH[header]
        : unknownShellCsvColumnCellKey(header);
      return cellErrors?.get(cellErrorKey(rowIndex, fieldPath)) ?? [];
    },
    [cellErrors, rowIndex]
  );

  const orderedHeaders = useMemo(
    () => [
      ...COMPANY_SHELL_IDENTITY_HEADERS,
      ...groups.flatMap((group) => group.headers),
    ],
    [groups]
  );

  useEffect(() => {
    if (!isOpen) return;
    const firstIssueHeader = orderedHeaders.find(
      (header) => fieldReasons(header).length > 0
    );
    const firstIssueInput = firstIssueHeader
      ? inputRefs.current.get(firstIssueHeader)
      : undefined;
    if (firstIssueInput) {
      firstIssueInput.focus({ preventScroll: true });
      return;
    }
    headingRef.current?.focus({ preventScroll: true });
  }, [fieldReasons, isOpen, orderedHeaders]);

  const renderField = (header: string) => {
    const column = columnsByHeader.get(header);
    if (!column) return null;

    const value = draft[header] ?? "";
    const required = column.requiredness === "required";
    const requiredMessage = `"${header}" is required.`;
    const reasons = inlineCompanyFieldIssues(header, fieldReasons(header));
    const visibleReasons =
      required && isBlank(value) && !reasons.includes(requiredMessage)
        ? [...reasons, requiredMessage]
        : reasons;
    const fieldId = `company-editor-${rowIndex}-${headerToIdFragment(header)}`;
    const reasonsId =
      visibleReasons.length > 0 ? `${fieldId}-issues` : undefined;
    const fieldPath = Object.prototype.hasOwnProperty.call(
      CSV_HEADER_TO_FIELD_PATH,
      header
    )
      ? CSV_HEADER_TO_FIELD_PATH[header]
      : undefined;
    const normalised = fieldPath
      ? normalisationsByCell.get(cellErrorKey(rowIndex, fieldPath))
      : undefined;
    const showLegend = isBlank(value) && column.legend;
    const legendText =
      column.requiredness === "required"
        ? column.legend
        : `(${column.legend.toLowerCase()})`;
    const legendClass =
      column.requiredness === "required"
        ? s.legendRequired
        : s.legendOptional;

    return (
      <div key={header} className={s.field}>
        <div className={s.fieldHead}>
          <label className={s.fieldLabel} htmlFor={fieldId}>
            {header}
          </label>
          {showLegend && <span className={legendClass}>{legendText}</span>}
        </div>
        <input
          ref={(element) => {
            if (element) inputRefs.current.set(header, element);
            else inputRefs.current.delete(header);
          }}
          id={fieldId}
          className={`${s.input} ${
            visibleReasons.length > 0 ? s.inputInvalid : ""
          }`}
          value={value}
          disabled={isProcessing}
          aria-required={required || undefined}
          aria-invalid={visibleReasons.length > 0 || undefined}
          aria-describedby={reasonsId}
          onChange={(event) =>
            onDraftChange({ ...draft, [header]: event.target.value })
          }
        />
        {normalised !== undefined && (
          <span className={s.normalised}>Normalised: {normalised}</span>
        )}
        {visibleReasons.length > 0 && (
          <ul id={reasonsId} className={s.fieldIssues}>
            {visibleReasons.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  const companyName = draft["Company Name"] || "Missing company name";
  const companyCode = draft["Company Code"] || "Missing company code";

  return (
    <Modal isOpen={isOpen}>
      <div className={s.modalContent}>
        <div className={s.modalHeader}>
          <Heading slot="title" ref={headingRef} tabIndex={-1}>
            Edit company
          </Heading>
          <p className={s.companyMeta}>
            {companyName} · {companyCode} · CSV row {sourceRow}
          </p>
        </div>

        <div className={s.modalBody}>
          {rowIssues.length > 0 && (
            <section
              className={s.issueSummary}
              aria-labelledby={`company-editor-${rowIndex}-issues-heading`}
            >
              <h3
                id={`company-editor-${rowIndex}-issues-heading`}
                className={s.issueSummaryTitle}
              >
                Issues to review
              </h3>
              <ul className={s.rowIssues}>
                {rowIssues.map((message) => (
                  <li key={message}>{message}</li>
                ))}
              </ul>
            </section>
          )}

          <section className={s.group} aria-labelledby="company-editor-identity">
            <h3 id="company-editor-identity" className={s.groupTitle}>
              Identity
            </h3>
            <div className={s.fields}>
              {COMPANY_SHELL_IDENTITY_HEADERS.map(renderField)}
            </div>
          </section>

          {groups.map((group) => (
            <section
              key={group.id}
              className={s.group}
              aria-labelledby={`company-editor-${group.id}`}
            >
              <h3
                id={`company-editor-${group.id}`}
                className={s.groupTitle}
              >
                {group.title}
              </h3>
              <div className={s.fields}>
                {group.headers.map(renderField)}
              </div>
            </section>
          ))}
        </div>

        <div className={s.modalFooter}>
          <ButtonGroup>
            <Button
              variant="tertiary"
              onPress={onCancel}
              isDisabled={isProcessing}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onPress={onSave}
              isDisabled={!isDirty || isProcessing}
            >
              Save changes
            </Button>
          </ButtonGroup>
        </div>
      </div>
    </Modal>
  );
}
